import React, { useState, useEffect } from "react";
import {

  Checkbox,
  IconButton,
  Stack,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextareaAutosize,
  Select,
  MenuItem,
  SelectChangeEvent,

} from "@mui/material";
import { styled } from "@mui/material/styles";
import right_arrow from "assets/right_arrow.svg";
import { ContentItem, NotesObj, StructuresItem, User } from "interfaces";
import "../StructuresTable/StructuresTable.css";
import alertOutlined from "assets/AlertOutlined.svg";
import bugOutlined from "assets/BugOutlined.svg";
import likeOutlined from "assets/LikeOutlined.svg";
import plusOutlined from "assets/PlusOutlined.svg";
import stopOutlined from "assets/StopOutlined.svg";
import checkOutlined from "assets/CheckOutlined.svg";

import { useNavigate } from "react-router-dom";

const Textarea = styled(TextareaAutosize)(
  ({ theme }) => `
    box-sizing: border-box;
    width: 100%;
    font-family: 'Work Sans', 'sans-serif';
    font-size: 16px;
    font-weight: 400;
    line-height: 1.5;
    padding: 8px 14px;
    border-radius: 8px;
    border-color:#B2BBC6;
    color:#1D242D;
    &:placeholder{
      color:#B2BBC6;
    }
    &:focus-visible {
      outline: 0;
    }
  `
);

const indicatorImageMap: { [key: string]: string } = {
  alert: alertOutlined,
  bug: bugOutlined,
  like: likeOutlined,
  done: checkOutlined,
  stop: stopOutlined,
};

const defaultNotes: { [key: string]: string } = {
  alert: "Alert needs immediate attention",
  bug: "Bug reported and needs fixing",
  like: "User feedback is positive",
  done: "New item added to the list",
  stop: "Process is halted due to issues",
};

interface RowProps {
  row: StructuresItem;
  isSelected: boolean;
  onSelect: (id: number) => void;
  onExpand: (id: number) => void;
  open: boolean;
  onIndicatorChange: (
    id: number,
    newIndicator: string,
    newNote: string
  ) => void;
  indicatorIcon: string;
  showCheckboxes: boolean;
}
const Row: React.FC<RowProps> = ({
  row,
  isSelected,
  onSelect,
  onExpand,
  open,
  onIndicatorChange,
  showCheckboxes,
}) => {
  const [view, setView] = useState("list");
  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    nextView: string
  ) => {
    setView(nextView);
    onIndicatorChange(row.id, nextView, note);
  };

  
  const [currentIndicator, setCurrentIndicator] = useState<string>(
    row.indicator || "alert"
  );

  const [note, setNote] = useState<string>(row.note || defaultNotes[currentIndicator]);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isIconPopupOpen, setIsIconPopupOpen] = useState(false);

  useEffect(() => {
    setCurrentIndicator(row.indicator || "alert");
    setNote(row.note || defaultNotes[row.indicator || "alert"]);
  }, [row.indicator, row.note]);
  


  const handleRowClick = (event: React.MouseEvent) => {
    if (!(event.target as HTMLElement).closest(".expand-button")) {
      onSelect(row.id);
    }
  };
  
  const handleNoteSave = () => {
    onIndicatorChange(row.id, currentIndicator, note); 
    setIsEditing(false); 
  };
  const navigate = useNavigate();

  const handleViewClick = () => {
    navigate(`/cms/structures/${row.id}`);
  };



  const handleIndicatorChange = (newIndicator: string) => {
    setCurrentIndicator(newIndicator);
    setNote(defaultNotes[newIndicator]);
    setIsPopupOpen(false);
    onIndicatorChange(row.id, newIndicator, defaultNotes[newIndicator]);
  };

  const handleIconClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsIconPopupOpen(true);
  };

  const handleIconChange = (newIndicator: string) => {
    setCurrentIndicator(newIndicator);
    onIndicatorChange(row.id, newIndicator, defaultNotes[newIndicator]);
    setIsIconPopupOpen(false);
  };

  
  const getStatusColor = (status: string) => {
    switch (status) {
      case "No textbooks":
        return "#FF0000";
      case "Needs Update":
        return "#F4C401";
      case "Up to date":
        return "#1F9C00";
      default:
        return "#7E7E7E";
    }
  };

  return (
    <React.Fragment>
      <TableRow
        onClick={handleRowClick}
        sx={{ "& > *": { borderBottom: "unset" } }}
      >
        <TableCell padding="checkbox">
          {showCheckboxes && (
            <Checkbox
              color="primary"
              checked={isSelected}
              onClick={(event) => {
                event.stopPropagation();
              }}
              onChange={() => onSelect(row.id)}
            />
          )}
        </TableCell>
        <TableCell align="left">{row.subject.name}</TableCell>
        <TableCell align="center">{row.expeditions}</TableCell>
        <TableCell
          align="center"
          style={{ color: getStatusColor(row.syllabus) }}
        >
          {row.syllabus}
        </TableCell>
        <TableCell
          align="center"
          style={{ color: getStatusColor(row.categories) }}
        >
          {row.categories}
        </TableCell>
        <TableCell align="center">
          {new Date(row.last_updated).toLocaleDateString()}
        </TableCell>

         <TableCell align="center" className="hoverable-indicator-cell">
          <div className="indicator-container">
            <img
              src={row.indicator}
              alt="Indicator"
              className="content-indicators"
            />
            <div className="hover-pill">
              <div className="pill-content">
                <div className="pill-date">
                  {new Date(row.last_updated).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                  })}
                </div>
                <div className="pill-body">
                  <img
                    src={row.indicator}
                    alt="Indicator Icon"
                    className="pill-icon"
                  />
                  <div className="pill-message">
                    This is your custom message.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TableCell>  
        <TableCell align="center">
          <img src={row.indicator2} alt="" className="content-indicators" />
        </TableCell>

        <TableCell align="center">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onExpand(row.id);
            }}
          >
            <img
              src={right_arrow}
              alt="View"
              onClick={handleViewClick}
            />
          </IconButton>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

interface StructuresTableProps {
  data: StructuresItem[];
  totalCount: number;
  fetchData: (page: number, itemsPerPage: number) => void;
  updateContent: (item: ContentItem) => void;
  onIndicatorChange: (id: number, newIndicator: string) => void;
setIndicators: (id: any) => void;
  onSelectedItemsChange: (selectedItems: number[]) => void;
  onExpandedItemChange: (expandedItem: number | null) => void;
}

const StructuresTable: React.FC<StructuresTableProps> = ({
  data,
  totalCount,
  fetchData,
  updateContent,
  onSelectedItemsChange,
  onExpandedItemChange,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [expandedItem, setExpandedItem] = useState<number | null>(null);
  const [showCheckboxes, setShowCheckboxes] = useState(false);

const indicatorImageMap: { [key: string]: string } = {
  alert: alertOutlined,
  bug: bugOutlined,
  like: likeOutlined,
  add: plusOutlined,
  stop: stopOutlined,
};
const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
  const newValue = Number(event.target.value);
  setItemsPerPage(newValue);
  setCurrentPage(1);
};

  useEffect(() => {
    fetchData(currentPage, itemsPerPage === -1 ? totalCount : itemsPerPage);
  }, [currentPage, itemsPerPage, fetchData, totalCount]);

  const handleUpdateUser = (item: ContentItem, newAssignedUser: User) => {
    updateContent({
      ...item,
      assigned: newAssignedUser,
    });
  };

  const handleUpdateDueDate = (item: ContentItem, newDate: Date) => {
    updateContent({
      ...item,
      assigned_due: newDate,
    });
  };

  const handleRaiseIssue = (item: ContentItem, newNotes: string) => {
    updateContent({
      ...item,
      issue: {
        ...item.issue,
        notes: newNotes,
      },
    });
  };

  const handleUpdateNotes = (
    item: ContentItem,
    noteType: keyof NotesObj,
    newNote: string,
    userId: number,
    date: string
  ) => {
    updateContent({
      ...item,
      notes: {
        ...item.notes,
        [noteType]: {
          ...item.notes[noteType],
          a: userId,
          c: date,
          d: newNote,
        },
      },
    });
  };

  const handleSelectAll = (event: { target: { checked: any } }) => {
    const visibleItems = data
      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
      .map((item) => item.id);

    const allVisibleSelected = visibleItems.every((id) =>
      selectedItems.includes(id)
    );

    const newSelectedItems = allVisibleSelected
      ? selectedItems.filter((id) => !visibleItems.includes(id))
      : Array.from(new Set([...selectedItems, ...visibleItems]));

    setSelectedItems(newSelectedItems);
    setShowCheckboxes(true);
    onSelectedItemsChange(newSelectedItems);
  };

  const handleSelectRow = (id: number) => {
    setSelectedItems((prevSelected) => {
      const updatedSelected = prevSelected.includes(id)
        ? prevSelected.filter((item) => item !== id)
        : [...prevSelected, id];
      onSelectedItemsChange(updatedSelected);
      setShowCheckboxes(true);
      return updatedSelected;
    });
  };

  const handleExpandRow = (id: number | null) => {
    const updatedExpandedItem = expandedItem === id ? null : id;
    setExpandedItem(updatedExpandedItem);
    onExpandedItemChange(updatedExpandedItem);
  };

  const handlePageChange = (
    event: any,
    value: React.SetStateAction<number>
  ) => {
    setCurrentPage(value);
  };

  return (
    <>
      <div className="structures-table-wrapper">
        <TableContainer>
          <Table aria-label="collapsible table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Checkbox
                    color="primary"
                    checked={selectedItems.length === data.length}
                    indeterminate={
                      selectedItems.length > 0 &&
                      selectedItems.length < data.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell align="left">Subject</TableCell>
                <TableCell align="center">Projects</TableCell>
                <TableCell align="center">Syllabus</TableCell>
                <TableCell align="center">Categories</TableCell>
                <TableCell align="center">Last Updated</TableCell>
                <TableCell align="center">Esme Notes</TableCell>
                <TableCell align="center">Martie Notes</TableCell>
                <TableCell align="center">View</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data
                .slice(
                  (currentPage - 1) * itemsPerPage,
                  currentPage * itemsPerPage
                )
                .map((row) => (
                  <Row
                    key={row.id}
                    row={row}
                    isSelected={selectedItems.includes(row.id)}
                    onSelect={handleSelectRow}
                    onExpand={handleExpandRow}
                    open={expandedItem === row.id}
                    indicatorIcon={indicatorImageMap[row.indicator]}
                    showCheckboxes={showCheckboxes}
                    onIndicatorChange={function (
                      id: number,
                      newIndicator: string
                    ): void {
                      throw new Error("Function not implemented.");
                    }}
                  />
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <div className="pagination-wrapper">
        <Select
          labelId="rows-per-page-label"
          id="rows-per-page"
          value={itemsPerPage}
          onChange={handleChangeRowsPerPage}
          className="select-per-page"
        >
          {[5, 10, 25].map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
          {totalCount <= 50 && <MenuItem value={-1}>All</MenuItem>}
        </Select>
        <Stack spacing={2}>
          <Pagination
            count={Math.ceil(
              totalCount / (itemsPerPage === -1 ? totalCount : itemsPerPage)
            )}
            shape="rounded"
            page={currentPage}
            onChange={handlePageChange}
          />
        </Stack>
      </div>
    </>
  );
};

export default StructuresTable;
